import React, { useState, useEffect } from 'react';
import HomeService from '../services/HomeService';
import {
    Modal, Button, Table, Form, Card, ListGroup, Tabs, Tab
} from 'react-bootstrap';
import { FaEye } from 'react-icons/fa';
import {
    AreaChart, Area,
    LineChart, Line,
    XAxis, YAxis,
    Tooltip, Legend,
    PieChart, Pie, Cell,
    ResponsiveContainer
} from 'recharts';

const SmartReportPage = () => {
    const [query, setQuery] = useState('');
    const [data, setData] = useState([]);
    const [loading, setLoading] = useState(false);
    const [showModal, setShowModal] = useState(false);
    const [selectedDescription, setSelectedDescription] = useState('');
    const [suggestions, setSuggestions] = useState([]);
    const [recentQueries, setRecentQueries] = useState([]);
    const [showChart, setShowChart] = useState(false);
    const [chartData, setChartData] = useState([]);
    const [chartType, setChartType] = useState('pie');
    const [activeTab, setActiveTab] = useState('data');

    const hintLibrary = [
        "show pending or approved reports with lcnumber, departmentname",
        "only show lcnumber, departmentname, statustext",
        "show top 5 records where status is approved",
        "show only lcnumber, description, actionon",
        "show records with lctype other and status approved",
        "show approved records from last 7 days with lcnumber, actionon",
        "show status wise summary in chart"
    ];

    const getNameOnly = (text) => {
        if (!text) return 'Unknown';
        if (text.toLowerCase().startsWith("pending at ")) return text.replace("Pending at ", "");
        if (text.toLowerCase().startsWith("final approved by ")) return text.replace("Final Approved by ", "");
        if (text.toLowerCase().startsWith("hold by ")) return text.replace("Hold by ", "");
        if (text.toLowerCase() === "sent to bank") return "Bank";
        return text;
    };

    const handleInputChange = (e) => {
        const value = e.target.value;
        setQuery(value);
        if (value.length > 2) {
            const filtered = hintLibrary.filter((h) =>
                h.toLowerCase().includes(value.toLowerCase())
            );
            setSuggestions(filtered);
        } else {
            setSuggestions([]);
        }
    };

    const handleSearch = async (e) => {
        e.preventDefault();
        if (!query.trim()) return alert("Please enter a query.");
        setLoading(true);
        setSuggestions([]);

        const lowerQuery = query.toLowerCase();

        // 👇 (Optional) Still extract chart type based on keywords
        let type = 'pie';
        if (/area\s?(chart|graph)?/.test(lowerQuery)) type = 'area';
        else if (/line\s?(chart|graph)?/.test(lowerQuery)) type = 'line';

        const payload = {
            query,
            pageNumber: 1,
            pageSize: 10,
            requestedBy: 0,
            showChart: true   // 🔥 Force chart to always show
        };

        try {
            const res = await HomeService.getSmartReport(payload);
            if (res?.data?.length > 0) {
                setData(res.data);
                setShowChart(true);           // 🔥 Always show chart
                setChartType(type);          // use detected or default type
                setActiveTab('data');       // 🧠 Optional: switch to graph tab by default
                const updated = [query, ...recentQueries.filter(q => q !== query)].slice(0, 5);
                localStorage.setItem("smart_recent_queries", JSON.stringify(updated));
                setRecentQueries(updated);
            } else {
                setData([]);
                setShowChart(false);
                alert("No records found. Try a more specific query.");
            }
        } catch (error) {
            console.error("Error:", error);
            alert("An error occurred while fetching data.");
        } finally {
            setLoading(false);
        }
    };


    useEffect(() => {
        if (showChart && data.length > 0) {
            const statusCount = data.reduce((acc, item) => {
                const key = getNameOnly(item.statustext);
                acc[key] = (acc[key] || 0) + 1;
                return acc;
            }, {});
            const transformed = Object.entries(statusCount).map(([status, count]) => ({ status, count }));
            setChartData(transformed);
        }
    }, [data, showChart]);

    return (
        <div className="container my-4">
            <h6 className="fw-bold text-primary mb-2">Smart Report Generator</h6>

            <Card className="shadow-sm mb-3">
                <Card.Body>
                    <Form onSubmit={handleSearch}>
                        <div className="row g-3 align-items-end">
                            <div className="col-md-9 position-relative">
                                <Form.Control
                                    type="text"
                                    placeholder="Ask in natural language..."
                                    value={query}
                                    onChange={handleInputChange}
                                    autoComplete="off"
                                />
                                {suggestions.length > 0 && (
                                    <ListGroup className="position-absolute w-100 shadow z-3">
                                        {suggestions.map((item, idx) => (
                                            <ListGroup.Item
                                                key={idx}
                                                action
                                                onClick={() => {
                                                    setQuery(item);
                                                    setSuggestions([]);
                                                }}
                                            >
                                                {item}
                                            </ListGroup.Item>
                                        ))}
                                    </ListGroup>
                                )}
                            </div>
                            <div className="col-md-3 d-grid">
                                <Button type="submit" variant="primary" disabled={loading}>
                                    {loading ? 'Searching...' : 'Search'}
                                </Button>
                            </div>
                        </div>
                    </Form>
                </Card.Body>
            </Card>

            {/* Tabs Section */}
            <Tabs
                id="smart-report-tabs"
                activeKey={activeTab}
                onSelect={(k) => setActiveTab(k)}
                className="mb-3"
            >
                <Tab eventKey="data" title="📋 Data">
                    {data.length > 0 ? (
                        <div className="table-responsive shadow-sm">
                            <Table bordered hover className="align-middle mb-0">
                                <thead className="table-light">
                                    <tr>
                                        {Object.keys(data[0]).map((key, idx) => (
                                            <th key={idx} className="text-capitalize">{key}</th>
                                        ))}
                                    </tr>
                                </thead>
                                <tbody>
                                    {data.map((row, idx) => (
                                        <tr key={idx}>
                                            {Object.entries(row).map(([key, val], i) => (
                                                <td key={i}>
                                                    {key.toLowerCase() === 'description' && val ? (
                                                        <Button
                                                            variant="outline-secondary"
                                                            size="sm"
                                                            onClick={() => {
                                                                setSelectedDescription(val);
                                                                setShowModal(true);
                                                            }}
                                                        >
                                                            <FaEye />
                                                        </Button>
                                                    ) : (
                                                        key.toLowerCase() !== 'description' ? val : null
                                                    )}
                                                </td>
                                            ))}
                                        </tr>
                                    ))}
                                </tbody>
                            </Table>
                        </div>
                    ) : (
                        !loading && <p className="text-muted">No data to display. Run a query.</p>
                    )}
                </Tab>

                <Tab eventKey="graph" title="📈 Graph">
                    {chartData.length > 0 ? (
                        <div className="p-3 rounded shadow bg-white border">
                            <div className="d-flex justify-content-between align-items-center mb-2">
                                <h6 className="fw-bold text-primary mb-0">
                                    {chartType.toUpperCase()} Chart
                                </h6>
                                <Form.Select
                                    style={{ width: '160px', fontSize: '0.85rem' }}
                                    value={chartType}
                                    onChange={(e) => setChartType(e.target.value)}
                                >
                                    <option value="pie">Pie Chart</option>
                                    <option value="area">Area Chart</option>
                                    <option value="line">Line Chart</option>
                                </Form.Select>
                            </div>

                            <ResponsiveContainer width="100%" height={220}>
                                {chartType === 'pie' ? (
                                    <PieChart margin={{ top: 10, right: 10, bottom: 10, left: 10 }}>
                                        <Tooltip />
                                        <Legend
                                            layout="horizontal"
                                            align="center"
                                            verticalAlign="bottom"
                                            iconSize={10}
                                            wrapperStyle={{ fontSize: '0.75rem' }}
                                        />
                                        <Pie
                                            data={chartData}
                                            dataKey="count"
                                            nameKey="status"
                                            cx="50%"
                                            cy="50%"
                                            outerRadius={70}
                                            labelLine={false}
                                            label={({ cx, cy, midAngle, innerRadius, outerRadius, value }) => {
                                                const RADIAN = Math.PI / 180;
                                                const radius = innerRadius + (outerRadius - innerRadius) / 2;
                                                const x = cx + radius * Math.cos(-midAngle * RADIAN);
                                                const y = cy + radius * Math.sin(-midAngle * RADIAN);
                                                return (
                                                    <text
                                                        x={x}
                                                        y={y}
                                                        fill="#ffffff"
                                                        textAnchor="middle"
                                                        dominantBaseline="central"
                                                        fontSize={10}
                                                        fontWeight="bold"
                                                    >
                                                        {value}
                                                    </text>
                                                );
                                            }}
                                        >
                                            {chartData.map((entry, i) => (
                                                <Cell
                                                    key={i}
                                                    fill={['#0d6efd', '#6f42c1', '#198754', '#fd7e14', '#dc3545'][i % 5]}
                                                />
                                            ))}
                                        </Pie>
                                    </PieChart>
                                ) : chartType === 'line' ? (
                                    <LineChart data={chartData} margin={{ top: 10, right: 10, bottom: 10, left: 0 }}>
                                        <XAxis
                                            dataKey="status"
                                            angle={-30}
                                            textAnchor="end"
                                            interval={0}
                                            height={40}
                                            tick={{ fontSize: 10 }}
                                        />
                                        <YAxis tick={{ fontSize: 10 }} />
                                        <Tooltip />
                                        <Legend wrapperStyle={{ fontSize: '0.75rem' }} />
                                        <Line
                                            type="monotone"
                                            dataKey="count"
                                            stroke="#198754"
                                            strokeWidth={2}
                                            dot={{ r: 3 }}
                                        />
                                    </LineChart>
                                ) : (
                                    <AreaChart data={chartData} margin={{ top: 10, right: 10, bottom: 10, left: 0 }}>
                                        <XAxis
                                            dataKey="status"
                                            angle={-30}
                                            textAnchor="end"
                                            interval={0}
                                            height={40}
                                            tick={{ fontSize: 10 }}
                                        />
                                        <YAxis tick={{ fontSize: 10 }} />
                                        <Tooltip />
                                        <Legend wrapperStyle={{ fontSize: '0.75rem' }} />
                                        <defs>
                                            <linearGradient id="colorCount" x1="0" y1="0" x2="0" y2="1">
                                                <stop offset="5%" stopColor="#0d6efd" stopOpacity={0.8} />
                                                <stop offset="95%" stopColor="#0d6efd" stopOpacity={0} />
                                            </linearGradient>
                                        </defs>
                                        <Area
                                            type="monotone"
                                            dataKey="count"
                                            stroke="#0d6efd"
                                            fill="url(#colorCount)"
                                            fillOpacity={1}
                                        />
                                    </AreaChart>
                                )}
                            </ResponsiveContainer>
                        </div>
                    ) : (
                        <p className="text-muted">No chart data available.</p>
                    )}
                </Tab>

            </Tabs>

            {/* Description Modal */}
            <Modal show={showModal} onHide={() => setShowModal(false)} size="lg" centered>
                <Modal.Header closeButton>
                    <Modal.Title>Description</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    <div className="ck-content" dangerouslySetInnerHTML={{ __html: selectedDescription }} />
                </Modal.Body>
                <Modal.Footer>
                    <Button variant="secondary" onClick={() => setShowModal(false)}>Close</Button>
                </Modal.Footer>
            </Modal>
        </div>
    );
};

export default SmartReportPage;
